import React from "react";
import { Building2, Users, ScrollText, Sparkles, Landmark, Coins } from "lucide-react";
import SeoLandingTemplate from "./SeoLandingTemplate";

/**
 * /en/dnd-city-generator · /it/dnd-city-generator
 * SEO landing for the "D&D city generator" query cluster.
 */
const data = {
  slug: "dnd-city-generator",
  path: "/dnd-city-generator",
  seoTitle: {
    en: "D&D City Generator — build living cities for your campaign in minutes",
    it: "Generatore di città D&D — costruisci città vive per la tua campagna in pochi minuti",
  },
  seoDescription: {
    en: "Generate D&D cities that feel alive: districts, guilds, notable NPCs, shops and rumours. Cartomancer turns each city marker into a full location dossier — with DM-only lore ready when the party arrives.",
    it: "Genera città D&D che sembrano vive: quartieri, gilde, NPC importanti, botteghe e voci di corridoio. Cartomancer trasforma ogni marker in un dossier completo — con lore riservata al DM pronta quando i giocatori arrivano.",
  },
  eyebrow: { en: "Mytharion · City generator", it: "Mytharion · Generatore di città" },
  h1: {
    en: "D&D City Generator — living cities in minutes, not evenings",
    it: "Generatore di città D&D — città vive in minuti, non serate",
  },
  lead: [
    {
      en: "Every campaign eventually needs a city the party can wander, shop, spy and drink in. Cartomancer makes each city a first-class object: districts, guilds, NPCs, shops, rumours — plus the DM-only secrets your players shouldn't see.",
      it: "Prima o poi ogni campagna ha bisogno di una città in cui i giocatori possano vagare, comprare, spiare e bere. Cartomancer rende ogni città un oggetto di prima classe: quartieri, gilde, NPC, botteghe, voci — più i segreti che i giocatori non devono vedere.",
    },
  ],
  primaryCta: {
    href: "/product/cartomancer_basic",
    label: { en: "Generate a city", it: "Genera una città" },
  },
  secondaryCta: {
    href: "/dnd-map-maker",
    label: { en: "See the map maker", it: "Vedi il creatore di mappe" },
  },
  featuresTitle: { en: "What every city marker holds", it: "Cosa contiene ogni marker città" },
  features: [
    {
      icon: Building2,
      title: { en: "Districts & wards", it: "Quartieri e distretti" },
      desc: {
        en: "Slums, docks, temple district, noble quarter — assign a mood and encounter table per district.",
        it: "Bassifondi, moli, quartiere dei templi, quartiere nobile — assegna atmosfera e tabella di incontri per quartiere.",
      },
    },
    {
      icon: Users,
      title: { en: "Notable NPCs", it: "NPC importanti" },
      desc: {
        en: "Attach the mayor, the guildmaster, the fence, the paladin who hates the party — with private DM motives.",
        it: "Aggiungi sindaco, capo-gilda, ricettatore, paladino che odia i giocatori — con motivazioni segrete per il DM.",
      },
    },
    {
      icon: Coins,
      title: { en: "Shops & prices", it: "Botteghe e prezzi" },
      desc: {
        en: "Track magic-item availability, black-market goods and unique local products.",
        it: "Tieni traccia di oggetti magici disponibili, mercato nero e prodotti locali unici.",
      },
    },
    {
      icon: Landmark,
      title: { en: "Guilds & factions", it: "Gilde e fazioni" },
      desc: {
        en: "Thieves' guild, mage's college, merchant princes — with faction-level plot hooks tied to each city.",
        it: "Gilda dei ladri, collegio di maghi, principi mercanti — con ganci narrativi legati a ogni città.",
      },
    },
    {
      icon: ScrollText,
      title: { en: "Rumours & tavern talk", it: "Voci e chiacchiere da taverna" },
      desc: {
        en: "A rotating table of rumours the players can overhear — perfect for kicking off side quests.",
        it: "Una tabella di voci che i giocatori possono origliare — perfetta per far partire side quest.",
      },
    },
    {
      icon: Sparkles,
      title: { en: "AI-assisted (Premium)", it: "Assistito dall'AI (Premium)" },
      desc: {
        en: "Ask the AI Loremaster to generate names, quirks, tavern menus and city-specific lore in seconds.",
        it: "Chiedi all'AI Loremaster di generare nomi, particolarità, menù di taverna e lore locale in pochi secondi.",
      },
    },
  ],
  how: [
    {
      title: { en: "Drop a city marker", it: "Aggiungi un marker città" },
      desc: {
        en: "One click on the map creates the city entity with all the fields ready to fill.",
        it: "Un click sulla mappa crea l'entità città con tutti i campi pronti da compilare.",
      },
    },
    {
      title: { en: "Fill only what matters", it: "Riempi solo ciò che conta" },
      desc: {
        en: "You don't need every district up front. Start with 3 NPCs and 2 rumours; expand as your players push.",
        it: "Non ti servono tutti i quartieri subito. Parti da 3 NPC e 2 voci; espandi quando i giocatori insistono.",
      },
    },
    {
      title: { en: "Reveal at the table", it: "Rivela al tavolo" },
      desc: {
        en: "Share the district your players are in, keep the rest hidden. Great for pacing intrigue.",
        it: "Condividi il quartiere in cui si trovano i giocatori, tieni nascosto il resto. Ottimo per dosare gli intrighi.",
      },
    },
  ],
  faq: [
    {
      q: { en: "Is this a random name generator or a full city builder?", it: "È un generatore di nomi casuali o un city builder completo?" },
      a: {
        en: "Both — the AI Loremaster (Premium) will generate names, quirks and rumours on demand, while the marker structure lets you build a full living city over time.",
        it: "Entrambi — l'AI Loremaster (Premium) genera nomi, particolarità e voci a richiesta, mentre la struttura del marker ti permette di costruire una città vera nel tempo.",
      },
    },
    {
      q: { en: "Does it work without the AI?", it: "Funziona senza l'AI?" },
      a: {
        en: "Yes. Every field is optional — the AI just accelerates the boring parts. Cartomancer Basic works standalone.",
        it: "Sì. Ogni campo è opzionale — l'AI velocizza solo le parti noiose. Cartomancer Basic funziona anche da solo.",
      },
    },
    {
      q: { en: "Can I share a city with my players?", it: "Posso condividere una città con i giocatori?" },
      a: {
        en: "Yes. Choose which fields are player-visible; DM notes stay private. When you launch a session in MytharionVTT, the city travels with you.",
        it: "Sì. Scegli quali campi sono visibili ai giocatori; le note DM restano private. Quando avvii una sessione in MytharionVTT, la città viaggia con te.",
      },
    },
  ],
  finalCta: {
    href: "/product/cartomancer_basic",
    headline: {
      en: "Give your players a city they'll want to come back to",
      it: "Dai ai tuoi giocatori una città in cui vorranno tornare",
    },
    label: { en: "Start with Cartomancer", it: "Parti con Cartomancer" },
  },
  software: { name: "Cartomancer", category: "DesignApplication" },
};

export default function DndCityGeneratorLanding() {
  return <SeoLandingTemplate data={data} />;
}
